import type { ComponentPropsWithoutRef, Ref } from 'react'
import { cn } from '#utils'

/**
 * Progress bar component
 *
 * Native `<div>` element pair (track + indicator) with proper ARIA
 * attributes for accessible progress indication.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/progressbar/
 *
 * @example
 * ```tsx
 * <Progress value={75} className="h-2" />
 * ```
 */
export interface ProgressProps extends ComponentPropsWithoutRef<'div'> {
  /** Current progress value (0–100) */
  value?: number
  /** Maximum value @default 100 */
  max?: number
  /**
   * Override classes for the fill (indicator) — e.g. `bg-theme-600` to follow the runtime
   * white-label theme. Merged over the default `bg-butter-brand-600` via tailwind-merge.
   */
  indicatorClassName?: string
}

export function Progress({ value = 0, max = 100, className, indicatorClassName, ref, ...props }: ProgressProps & { ref?: Ref<HTMLDivElement> }) {
  // Guard against invalid max values (prevent division by zero/negative)
  const safeMax = Math.max(max, 1)
  
  // Clamp value to valid range [0, safeMax]
  const clampedValue = Math.min(Math.max(value, 0), safeMax)
  
  // Calculate percentage (will always be valid now)
  const percent = (clampedValue / safeMax) * 100

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      className={cn(
        'relative h-4 w-full overflow-hidden rounded-full bg-butter-gray-200',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'h-full bg-butter-brand-600 transition-all duration-300 ease-in-out',
          indicatorClassName,
        )}
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}
